import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { API_BASE } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  CheckCircle, XCircle, PaperclipHorizontal, DownloadSimple,
  Clock, ArrowLeft, PaperPlaneTilt, Trash, PencilSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  'Draft': 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]',
  'Pending Accounting Manager Approval': 'bg-[#FEF9C3] text-[#854D0E] border-[#EAB308]',
  'Pending General Manager Approval': 'bg-[#FEF9C3] text-[#854D0E] border-[#EAB308]',
  'Approved': 'bg-[#DCFCE7] text-[#166534] border-[#22C55E]',
  'Rejected': 'bg-[#FEE2E2] text-[#991B1B] border-[#EF4444]',
  'Cancelled': 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'
};

const fmt = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(n || 0);

export default function PaymentRequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [pr, setPr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState(null); // 'approve' | 'reject'
  const [remarks, setRemarks] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [hardCopyChecking, setHardCopyChecking] = useState(false);

  const fetchData = () => {
    setLoading(true);
    api.get(`/payments/${id}`).then(r => setPr(r.data)).catch(() => toast.error('Request not found')).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [id]);


  const markHardCopyReceived = async () => {
    if (!pr?.id) return;
    setHardCopyChecking(true);
    try {
      const { data } = await api.post(`/payments/${pr.id}/hard-copy-received`);
      setPr(data);
      toast.success('Hard copy marked as received');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark hard copy received');
    } finally {
      setHardCopyChecking(false);
    }
  };

  const handleApprovalAction = async () => {
    if (actionDialog === 'reject' && !remarks.trim()) {
      toast.error('Rejection remarks are required');
      return;
    }
    setActionLoading(true);
    try {
      await api.post(`/payments/${id}/approve`, { action: actionDialog, remarks });
      toast.success(`Request ${actionDialog === 'approve' ? 'approved' : 'rejected'}`);
      setActionDialog(null);
      setRemarks('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await api.post(`/payments/${id}/submit`);
      toast.success('Request submitted for approval');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    }
  };

  const handleCancel = async () => {
    try {
      await api.post(`/payments/${id}/cancel`);
      toast.success('Request cancelled');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancel failed');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/payments/${id}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('File uploaded');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-8 w-64 bg-muted rounded-sm" /><div className="h-64 bg-muted rounded-sm" /></div>;
  if (!pr) return <div className="text-center py-12 text-[#475569]">Request not found</div>;

  const canApprove = (
    (user?.role === 'accounting_manager' && pr.status === 'Pending Accounting Manager Approval') ||
    (user?.role === 'general_manager' && pr.status === 'Pending General Manager Approval')
  );
  const canSubmit = user?.role === 'ap_accounting' && pr.submitted_by === user?.id && ['Draft', 'Rejected'].includes(pr.status);
  const canEdit = user?.role === 'ap_accounting' && pr.submitted_by === user?.id && ['Draft', 'Rejected'].includes(pr.status);
  const canCancel = user?.role === 'ap_accounting' && pr.submitted_by === user?.id && !['Approved', 'Cancelled'].includes(pr.status);
  const canUpload = user?.role === 'ap_accounting' && pr.status !== 'Approved';

  const handleEditResubmit = () => {
    navigate('/payments/create', {
      state: {
        editData: {
          id: pr.id,
          request_code: pr.request_code,
          vendor_id: pr.vendor_id,
          contract_id: pr.contract_id,
          category: pr.category,
          amount: pr.amount,
          description: pr.description,
          due_date: pr.due_date,
          payment_type: pr.payment_type,
          notes: pr.notes
        }
      }
    });
  };

  // Get rejection remarks
  const rejectionApproval = pr.approvals?.find(a => a.action === 'reject');

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="payment-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft size={16} className="mr-1" /> Back
        </Button>
      </div>

      {/* Rejection Banner */}
      {pr.status === 'Rejected' && rejectionApproval && (
        <div className="p-4 rounded-sm bg-[#FEE2E2] border border-[#EF4444]/30" data-testid="rejection-banner">
          <div className="flex items-start gap-2">
            <XCircle size={20} className="text-[#991B1B] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-[#991B1B]">Request Rejected by {rejectionApproval.approver_name}</p>
              <p className="text-sm text-[#991B1B] mt-1">{rejectionApproval.remarks}</p>
              <p className="text-xs text-[#991B1B]/70 mt-1">{rejectionApproval.action_at?.slice(0, 16).replace('T', ' ')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{pr.request_code}</h1>
          <p className="text-sm text-[#475569] mt-1">{pr.description}</p>
        </div>
        <div className={`px-3 py-1.5 rounded-sm border text-xs uppercase tracking-[0.05em] font-bold ${STATUS_COLORS[pr.status]}`}>
          {pr.status}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 flex-wrap">
        
      {user?.role === 'ap_accounting' && ['Submitted', 'Assigned', 'Pending AP Processor Review'].includes(pr.status) && (
        <Card className="rounded-sm border border-[#EAB308]/30 bg-[#FEFCE8]">
          <CardHeader>
            <CardTitle className="text-base font-semibold">AP Accounting Hard Copy Validation</CardTitle>
          </CardHeader>
          <CardContent>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!pr.hard_copy_received}
                disabled={!!pr.hard_copy_received || hardCopyChecking}
                onChange={markHardCopyReceived}
                className="mt-1 h-4 w-4"
                data-testid="hard-copy-received-checkbox"
              />
              <div>
                <div className="font-medium text-sm">Hard Copy Received</div>
                <div className="text-xs text-[#64748B]">
                  AP Accounting must confirm receipt of the physical documents before submitting this request to the next approval level.
                </div>
                {pr.hard_copy_received && (
                  <div className="text-xs text-[#166534] mt-1">
                    Confirmed {pr.hard_copy_received_at ? new Date(pr.hard_copy_received_at).toLocaleString() : ''}
                  </div>
                )}
              </div>
            </label>
          </CardContent>
        </Card>
      )}

{canApprove && (
          <>
            <Button className="rounded-sm bg-[#22C55E] hover:bg-[#16A34A] gap-2" onClick={() => setActionDialog('approve')} data-testid="approve-btn">
              <CheckCircle size={16} /> Approve
            </Button>
            <Button variant="destructive" className="rounded-sm gap-2" onClick={() => setActionDialog('reject')} data-testid="reject-btn">
              <XCircle size={16} /> Reject
            </Button>
          </>
        )}
        {canEdit && (
          <Button className="rounded-sm bg-[#0055FF] hover:bg-[#0043CC] gap-2" onClick={handleEditResubmit} data-testid="edit-resubmit-btn">
            <PencilSimple size={16} /> Edit & Resubmit
          </Button>
        )}
        {canSubmit && pr.status === 'Draft' && (
          <Button variant="outline" className="rounded-sm gap-2 border-[#0055FF] text-[#0055FF]" onClick={handleSubmit} data-testid="submit-for-approval-btn">
            <PaperPlaneTilt size={16} /> Submit for Approval
          </Button>
        )}
        {canCancel && (
          <Button variant="outline" className="rounded-sm gap-2 text-red-600" onClick={handleCancel} data-testid="cancel-request-btn">
            <Trash size={16} /> Cancel Request
          </Button>
        )}
        {canUpload && (
          <>
            <Button variant="outline" className="rounded-sm gap-2" onClick={() => fileInputRef.current?.click()} data-testid="upload-file-btn">
              <PaperclipHorizontal size={16} /> Attach File
            </Button>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Details */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="rounded-sm border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Request Information</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Vendor</span><Link to={`/vendors/${pr.vendor_id}`} className="text-[#0055FF] hover:underline">{pr.vendor_name} ({pr.vendor_code})</Link></div>
                <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Category</span>{pr.category}</div>
                <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Amount</span><span className="text-lg font-bold">{fmt(pr.amount)}</span></div>
                <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Due Date</span>{pr.due_date}</div>
                <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Payment Type</span>{pr.payment_type}</div>
                <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Submitted By</span>{pr.submitted_by_name}</div>
                {pr.contract_id && <div className="col-span-2"><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Contract</span><Link to={`/contracts/${pr.contract_id}`} className="text-[#0055FF] hover:underline">{pr.contract_code} - {pr.contract_title}</Link></div>}
                {pr.notes && <div className="col-span-2"><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Notes</span>{pr.notes}</div>}
              </div>
            </CardContent>
          </Card>

          {/* Attachments - always visible so approvers can see documents */}
          <Card className="rounded-sm border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Supporting Documents ({pr.attachments?.length || 0})</CardTitle></CardHeader>
            <CardContent className={pr.attachments?.length > 0 ? "p-0" : ""}>
              {pr.attachments?.length > 0 ? (
                <Table>
                  <TableHeader><TableRow><TableHead className="text-[10px] uppercase tracking-[0.1em]">File</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Type</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]">Uploaded</TableHead><TableHead className="text-[10px] uppercase tracking-[0.1em]"></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {pr.attachments.map(a => (
                      <TableRow key={a.id}>
                        <TableCell className="text-sm">{a.file_name}</TableCell>
                        <TableCell className="text-sm text-[#475569]">{a.file_type}</TableCell>
                        <TableCell className="text-sm text-[#475569]">{a.uploaded_at?.slice(0, 10)}</TableCell>
                        <TableCell>
                          <a href={`${API_BASE}/api/files/${a.stored_name}`} target="_blank" rel="noopener noreferrer" className="text-[#0055FF] hover:underline text-sm flex items-center gap-1">
                            <DownloadSimple size={14} /> Download
                          </a>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-[#94A3B8] text-center py-4">No supporting documents attached</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Approval Timeline */}
        <div className="space-y-4">
          <Card className="rounded-sm border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Approval Timeline</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Created */}
                <TimelineItem
                  icon={<div className="w-3 h-3 rounded-full bg-[#0055FF]" />}
                  title="Created"
                  subtitle={pr.submitted_by_name}
                  date={pr.created_at?.slice(0, 16).replace('T', ' ')}
                />
                {/* Submitted */}
                {pr.submitted_at && (
                  <TimelineItem
                    icon={<div className="w-3 h-3 rounded-full bg-[#0055FF]" />}
                    title="Submitted for Approval"
                    date={pr.submitted_at?.slice(0, 16).replace('T', ' ')}
                  />
                )}
                {/* Approvals */}
                {pr.approvals?.map((a, i) => (
                  <TimelineItem
                    key={i}
                    icon={<div className={`w-3 h-3 rounded-full ${a.action === 'approve' ? 'bg-[#22C55E]' : 'bg-[#EF4444]'}`} />}
                    title={`${a.action === 'approve' ? 'Approved' : 'Rejected'} by ${a.approver_name}`}
                    subtitle={a.remarks || ''}
                    date={a.action_at?.slice(0, 16).replace('T', ' ')}
                    roleLabel={a.approver_role === 'accounting_manager' ? 'Accounting Manager' : 'General Manager'}
                  />
                ))}
                {/* Current status indicator */}
                {['Pending Accounting Manager Approval', 'Pending General Manager Approval'].includes(pr.status) && (
                  <TimelineItem
                    icon={<div className="w-3 h-3 rounded-full bg-[#EAB308] animate-pulse" />}
                    title={pr.status === 'Pending Accounting Manager Approval' ? 'Awaiting Accounting Manager' : 'Awaiting General Manager'}
                    subtitle="Pending review"
                  />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-sm border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Timestamps</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2">
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Created</span>{pr.created_at?.slice(0, 16).replace('T', ' ')}</div>
              {pr.submitted_at && <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Submitted</span>{pr.submitted_at?.slice(0, 16).replace('T', ' ')}</div>}
              <div><span className="text-[10px] uppercase tracking-[0.1em] text-[#94A3B8] block">Last Updated</span>{pr.updated_at?.slice(0, 16).replace('T', ' ')}</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Approval Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={() => { setActionDialog(null); setRemarks(''); }}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {actionDialog === 'approve' ? 'Approve Request' : 'Reject Request'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-[#475569] mb-3">
              {actionDialog === 'approve'
                ? `You are about to approve ${pr.request_code} for ${fmt(pr.amount)}.`
                : `You are about to reject ${pr.request_code}. Rejection remarks are required.`
              }
            </p>
            <Textarea
              value={remarks}
              onChange={e => setRemarks(e.target.value)}
              placeholder={actionDialog === 'reject' ? 'Enter rejection reason (required)' : 'Add remarks (optional)'}
              className="rounded-sm"
              rows={3}
              data-testid="approval-remarks-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setActionDialog(null); setRemarks(''); }}>Cancel</Button>
            <Button
              className={`rounded-sm ${actionDialog === 'approve' ? 'bg-[#22C55E] hover:bg-[#16A34A]' : 'bg-[#EF4444] hover:bg-[#DC2626]'}`}
              onClick={handleApprovalAction}
              disabled={actionLoading || (user?.role === "ap_accounting" && ["Submitted", "Assigned", "Pending AP Processor Review"].includes(pr.status) && !pr.hard_copy_received)}
              data-testid="confirm-action-btn"
            >
              {actionLoading ? 'Processing...' : actionDialog === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TimelineItem({ icon, title, subtitle, date, roleLabel }) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="mt-1">{icon}</div>
        <div className="flex-1 w-px bg-[#E2E8F0] mt-1" />
      </div>
      <div className="pb-4">
        <p className="text-sm font-medium text-[#0F172A]">{title}</p>
        {roleLabel && <span className="text-[10px] uppercase tracking-[0.1em] text-[#0055FF] font-medium">{roleLabel}</span>}
        {subtitle && <p className="text-xs text-[#475569] mt-0.5">{subtitle}</p>}
        {date && <p className="text-[10px] text-[#94A3B8] mt-0.5">{date}</p>}
      </div>
    </div>
  );
}
